import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";
import { setAuthenticatedUser } from "../../auth/service";
import { AppContext } from "../../context";
import storeApi from "../storeApi";

const sessionUserFromResponse = (response) => {
  if (!response || typeof response !== "object") return null;

  const root = response.data && response.success !== undefined ? response.data : response;

  return root.user || response.user || null;
};

const PINCODE_RE = /^\d{6}$/;

const buildFormFromUser = (user, buyer) => ({
  name: user?.name ?? "",
  company_name: buyer?.name ?? "",
  gst_number: buyer?.gst_number ?? "",
  billing_address: buyer?.billing_address ?? "",
  billing_pincode: buyer?.billing_pincode ?? "",
});

const initialsFromName = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (first + last).toUpperCase() || "?";
};

export default function StoreProfileForm({ layout = "page", onSaved }) {
  const { currentUser, setCurrentUser } = useContext(AppContext) || {};
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [form, setForm] = useState(buildFormFromUser(null, null));
  const [errors, setErrors] = useState({});
  const [noGstChecked, setNoGstChecked] = useState(false);
  const [agreedToNoGst, setAgreedToNoGst] = useState(false);

  const refreshSession = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await storeApi.sessionAPI.getCurrentSession();
      const user = sessionUserFromResponse(response);
      if (user && typeof setCurrentUser === "function") {
        setCurrentUser(user);
      }
    } catch (e) {
      setLoadError(
        e?.response?.data?.error?.message ||
          e?.message ||
          "Unable to load profile."
      );
    } finally {
      setLoading(false);
    }
  }, [setCurrentUser]);

  useEffect(() => {
    if (currentUser?.buyer) return;
    refreshSession();
  }, [currentUser?.buyer, refreshSession]);

  const buyer = currentUser?.buyer ?? null;

  useEffect(() => {
    setForm(buildFormFromUser(currentUser, buyer));
    setErrors({});
    setSaveError(null);
    setNoGstChecked(false);
    setAgreedToNoGst(false);
  }, [
    currentUser?.name,
    buyer?.name,
    buyer?.gst_number,
    buyer?.billing_address,
    buyer?.billing_pincode,
  ]);

  useEffect(() => {
    if (!savedFlash) return undefined;
    const t = setTimeout(() => setSavedFlash(false), 2500);
    return () => clearTimeout(t);
  }, [savedFlash]);

  const isDirty = useMemo(() => {
    const baseline = buildFormFromUser(currentUser, buyer);
    const baselineNoGst = false;
    return (
      baseline.name !== form.name ||
      baseline.company_name !== form.company_name ||
      baseline.gst_number !== form.gst_number ||
      baseline.billing_address !== form.billing_address ||
      baseline.billing_pincode !== form.billing_pincode ||
      noGstChecked !== baselineNoGst
    );
  }, [currentUser, buyer, form, noGstChecked]);

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
    setSavedFlash(false);
  };

  const handleBillingPincodeChange = (event) => {
    const raw = event?.target?.value ?? "";
    const next = raw.replace(/\D/g, "").slice(0, 6);
    setForm((prev) => ({ ...prev, billing_pincode: next }));
    setErrors((prev) =>
      prev.billing_pincode ? { ...prev, billing_pincode: null } : prev
    );
    setSavedFlash(false);
  };

  const validate = () => {
    const next = {};
    const name = form.name.trim();
    if (name.length > 0 && name.length < 2) {
      next.name = "Name must be at least 2 characters.";
    }

    const companyName = form.company_name.trim();
    if (companyName.length > 0 && companyName.length < 2) {
      next.company_name = "Company name must be at least 2 characters.";
    }

    const billingAddress = form.billing_address.trim();
    if (!billingAddress) {
      next.billing_address = "Billing address is required.";
    }

    const pin = form.billing_pincode.trim();
    if (!pin) {
      next.billing_pincode = "Billing pincode is required.";
    } else if (!PINCODE_RE.test(pin)) {
      next.billing_pincode = "Enter a valid 6-digit pincode.";
    }

    const gst = form.gst_number.trim();
    if (noGstChecked) {
      if (!agreedToNoGst) {
        next.no_gst_agreement = "Please agree to continue without a GSTIN.";
      }
    } else if (!gst) {
      next.gst_number = "GSTIN is required.";
    } else if (gst.length > 32) {
      next.gst_number = "GSTIN is too long.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const hasRequiredBillingFields = useMemo(() => {
    const billingAddress = form.billing_address.trim();
    const pin = form.billing_pincode.trim();
    const gst = form.gst_number.trim();
    const gstSatisfied = noGstChecked ? agreedToNoGst : gst.length > 0;

    return (
      billingAddress.length > 0 && pin.length > 0 && gstSatisfied
    );
  }, [
    form.billing_address,
    form.billing_pincode,
    form.gst_number,
    noGstChecked,
    agreedToNoGst,
  ]);

  const canSave = isDirty && hasRequiredBillingFields;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!validate()) return;

    const body = {
      name: form.name.trim(),
      company_name: form.company_name.trim(),
      gst_number: noGstChecked ? "" : form.gst_number.trim(),
      billing_address: form.billing_address.trim(),
      billing_pincode: form.billing_pincode.trim(),
    };

    setSaving(true);
    setSaveError(null);
    setSavedFlash(false);
    try {
      const response = await storeApi.authAPI.updateProfile(body);
      let user = sessionUserFromResponse(response);

      if (!user) {
        const sessionResponse = await storeApi.sessionAPI.getCurrentSession();
        user = sessionUserFromResponse(sessionResponse);
      }

      if (user && typeof setCurrentUser === "function") {
        const token =
          localStorage.getItem("storeAccessToken") ||
          localStorage.getItem("accessToken");
        if (token) {
          setAuthenticatedUser({ token, user });
        }
        setCurrentUser(user);
      }
      setSavedFlash(true);
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      setSaveError(
        e?.response?.data?.error?.message ||
          e?.error?.message ||
          e?.message ||
          "Unable to save profile."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(buildFormFromUser(currentUser, buyer));
    setErrors({});
    setSaveError(null);
    setSavedFlash(false);
    setNoGstChecked(false);
    setAgreedToNoGst(false);
  };

  const handleNoGstChange = (event) => {
    const checked = event.target.checked;
    setNoGstChecked(checked);
    setAgreedToNoGst(false);
    if (checked) {
      setForm((prev) => ({ ...prev, gst_number: "" }));
      setErrors((prev) =>
        prev.gst_number ? { ...prev, gst_number: null } : prev
      );
    }
    setSavedFlash(false);
  };

  const handleGstChange = (event) => {
    const value = event?.target?.value ?? "";
    if (value.trim()) {
      setNoGstChecked(false);
      setAgreedToNoGst(false);
    }
    handleChange("gst_number")(event);
  };

  if (!currentUser?.id) {
    return (
      <div className="alert alert-warning mb-0" role="alert">
        Please sign in to edit your profile.
      </div>
    );
  }

  if (currentUser?.dealer || currentUser?.admin) {
    return (
      <div className="alert alert-info mb-0" role="alert">
        Profile editing is available for buyer accounts only.
      </div>
    );
  }

  const displayName = form.name || currentUser?.name || "—";
  const initials = initialsFromName(displayName === "—" ? "" : displayName);
  const mobile = currentUser?.mobile;
  const isModal = layout === "modal";
  const showNoGstOption = form.gst_number.trim() === "";

  const actionButtons = (
    <div
      className={
        isModal
          ? "d-flex justify-content-end gap-2 pt-3 border-top mt-3"
          : "card-footer bg-white border-top d-flex justify-content-end gap-2"
      }
    >
      <Button
        type="button"
        color="link"
        className="text-muted text-decoration-none"
        disabled={saving || !canSave}
        onClick={handleReset}
      >
        Discard
      </Button>
      <Button
        type="submit"
        color="primary"
        disabled={saving || !canSave}
        className="px-4"
      >
        {saving ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
            Saving
          </>
        ) : (
          "Save changes"
        )}
      </Button>
    </div>
  );

  return (
    <>
      {loadError ? (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      ) : null}

      {loading && !buyer ? (
        <div className="text-muted">Loading...</div>
      ) : (
        <>
          <div
            className={
              isModal
                ? "d-flex align-items-center mb-3 pb-3 border-bottom"
                : "card border-0 shadow-sm mb-3"
            }
          >
            <div className={isModal ? "d-flex align-items-center w-100" : "card-body d-flex align-items-center"}>
              <div
                className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center fw-semibold me-3"
                style={{ width: 56, height: 56, fontSize: 20 }}
                aria-hidden="true"
              >
                {initials}
              </div>
              <div className="flex-grow-1 min-w-0">
                <div className="fw-semibold text-truncate">{displayName}</div>
                {mobile ? (
                  <div className="text-muted small mt-1">
                    <i className="fa-solid fa-phone me-1" />
                    {mobile}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <Form
            onSubmit={handleSubmit}
            noValidate
            className={isModal ? "" : "card border-0 shadow-sm mb-3"}
          >
            <div className={isModal ? "" : "card-body"}>
              <FormGroup>
                <Label for="profile-name" className="fw-semibold mb-1">
                  Your name
                </Label>
                <Input
                  id="profile-name"
                  type="text"
                  value={form.name}
                  onChange={handleChange("name")}
                  placeholder="Your name"
                  autoComplete="name"
                  invalid={!!errors.name}
                />
                {errors.name ? (
                  <div className="invalid-feedback d-block">
                    {errors.name}
                  </div>
                ) : null}
              </FormGroup>

              <FormGroup>
                <Label for="profile-company-name" className="fw-semibold mb-1">
                  Company name
                </Label>
                <Input
                  id="profile-company-name"
                  type="text"
                  value={form.company_name}
                  onChange={handleChange("company_name")}
                  placeholder="Company name"
                  autoComplete="organization"
                  invalid={!!errors.company_name}
                />
                {errors.company_name ? (
                  <div className="invalid-feedback d-block">
                    {errors.company_name}
                  </div>
                ) : null}
              </FormGroup>

              <FormGroup>
                <Label for="profile-gst-number" className="fw-semibold mb-1">
                  GSTIN <span className="text-danger">*</span>
                </Label>
                <Input
                  id="profile-gst-number"
                  type="text"
                  value={form.gst_number}
                  onChange={handleGstChange}
                  placeholder="22AAAAA0000A1Z5"
                  autoCapitalize="characters"
                  maxLength={32}
                  invalid={!!errors.gst_number}
                  disabled={noGstChecked}
                />
                {errors.gst_number ? (
                  <div className="invalid-feedback d-block">
                    {errors.gst_number}
                  </div>
                ) : null}
              </FormGroup>

              {showNoGstOption ? (
                <FormGroup check className="mb-3">
                  <Input
                    id="profile-no-gst"
                    type="checkbox"
                    checked={noGstChecked}
                    onChange={handleNoGstChange}
                  />
                  <Label check for="profile-no-gst" className="small">
                    I don't have GST registration
                  </Label>
                </FormGroup>
              ) : null}

              {noGstChecked ? (
                <div
                  className="rounded p-3 mb-3"
                  style={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffecb5",
                  }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <Input
                      id="profile-no-gst-agree"
                      type="checkbox"
                      className="mt-1 flex-shrink-0"
                      checked={agreedToNoGst}
                      onChange={(event) => {
                        setAgreedToNoGst(event.target.checked);
                        setErrors((prev) =>
                          prev.no_gst_agreement
                            ? { ...prev, no_gst_agreement: null }
                            : prev
                        );
                        setSavedFlash(false);
                      }}
                    />
                    <p className="small text-dark mb-0">
                      I understand that I still need to pay GST on my purchases, but cannot claim input tax credits.
                    </p>
                  </div>
                  {errors.no_gst_agreement ? (
                    <div className="text-danger small mt-2">
                      {errors.no_gst_agreement}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <FormGroup>
                <Label
                  for="profile-billing-address"
                  className="fw-semibold mb-1"
                >
                  Billing address <span className="text-danger">*</span>
                </Label>
                <Input
                  id="profile-billing-address"
                  type="textarea"
                  rows={3}
                  value={form.billing_address}
                  onChange={handleChange("billing_address")}
                  placeholder="House / building, street, area, city, state"
                  invalid={!!errors.billing_address}
                />
                {errors.billing_address ? (
                  <div className="invalid-feedback d-block">
                    {errors.billing_address}
                  </div>
                ) : null}
              </FormGroup>

              <FormGroup className="mb-0">
                <Label
                  for="profile-billing-pincode"
                  className="fw-semibold mb-1"
                >
                  Billing pincode <span className="text-danger">*</span>
                </Label>
                <Input
                  id="profile-billing-pincode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="postal-code"
                  maxLength={6}
                  value={form.billing_pincode}
                  onChange={handleBillingPincodeChange}
                  placeholder="600001"
                  invalid={!!errors.billing_pincode}
                  style={{ maxWidth: "12rem" }}
                />
                {errors.billing_pincode ? (
                  <div className="invalid-feedback d-block">
                    {errors.billing_pincode}
                  </div>
                ) : null}
              </FormGroup>
            </div>

            {saveError ? (
              <div
                className={`alert alert-danger py-2 ${isModal ? "mt-3 mb-0" : "mx-3 mt-0"}`}
                role="alert"
              >
                <i className="fa-solid fa-triangle-exclamation me-1" />
                {saveError}
              </div>
            ) : null}
            {savedFlash && !saveError ? (
              <div
                className={`alert alert-success py-2 ${isModal ? "mt-3 mb-0" : "mx-3 mt-0"}`}
                role="status"
                aria-live="polite"
              >
                <i className="fa-solid fa-circle-check me-1" />
                Profile saved.
              </div>
            ) : null}

            {actionButtons}
          </Form>
        </>
      )}
    </>
  );
}
